import {
  HorizontalTabs,
  HorizontalTabsButton,
  HorizontalTabsList,
  HorizontalTabsPanel,
  HorizontalTabsPanelList,
  Secondary,
  Text,
} from '@cinch-labs/design-system'
import { useSsrExperiments } from '@cinch-nx/adobe-server-side'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { Datadog } from '@cinch-nx/landing-ui-shared'
import {
  TrackingEvent,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { datadogRum } from '@datadog/browser-rum'
import cx from 'classnames'
import React from 'react'
import { HomepageVehiclesData } from '../../../types/feature-home-page'
import { VehicleCondition } from '../../../types/vehicles-data'
import { Filters } from '../filters/filters'
import { HeroPartEx } from '../hero-part-ex/hero-part-ex'
import { SearchCarsForm } from '../search-cars-form/search-cars-form'
import styles from './hero-tabs.module.css'

const { Actions, Context } = Datadog

export interface HeroTabsProps {
  title: string
  label: string
  placeholder: string
  ctaText: string
  partExTextList: string[]
  vehiclesData: HomepageVehiclesData
  section?: string
}

const tabsClickEvent = (): TrackingEvent => ({
  eventName: 'home-page-tab-click',
  type: TrackingEventTypes.ADOBE,
  data: {
    event: {
      name: 'click',
      category: 'hp part ex',
      action: 'select',
      value: 'hp part ex tab',
    },
  },
})

export const HeroTabs: React.FC<HeroTabsProps> = ({
  title,
  label,
  placeholder,
  ctaText,
  partExTextList,
  vehiclesData,
  section,
}) => {
  const { trackEvent } = useAnalytics()
  const { getExperienceIdForExperiment } = useSsrExperiments()

  const useValuationTerminology =
    getExperienceIdForExperiment(
      readFromEnv(Env.NavbarValuationExperimentId),
    ) === 1

  const isHomeHeroFormWithNewCarsTab =
    readFromEnv(Env.HomeHeroFormWithNewCarsTab) === 'true'
  const shouldShowNewCarsCTA = readFromEnv(Env.EnableNewCars) === 'true'

  const handleTrackEvent = (tabName: string) => {
    const normalizedTabName = tabName.toLowerCase()

    datadogRum.addAction(Actions.CLICK_HMC_LINK_IN_FILTERS_SECTION, {
      service: Context.HERO_HMC,
      squad: Context.SQUAD,
      page: '/',
      category: 'hero',
      label: `help-me-choose-click - inside ${normalizedTabName}`,
      value: `help-me-choose-click`,
    })

    trackEvent({
      eventName: `Help me choose inside ${normalizedTabName} - click`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: 'click',
          category: 'hero',
          label: `inside ${normalizedTabName}`,
          action: 'Help me choose',
        },
      },
    })
  }

  const handleShopNewCarsClick = () => {
    trackEvent({
      eventName: 'Shop new cars - click',
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: 'click',
          category: 'hero',
          label: 'shop new cars',
          action: 'navigate',
        },
      },
    })
  }

  const usedCarsTabLabel = isHomeHeroFormWithNewCarsTab
    ? 'Used cars'
    : 'Find a car'
  const newCarsTabLabel = isHomeHeroFormWithNewCarsTab
    ? 'New cars'
    : useValuationTerminology
    ? 'Value my car'
    : 'Part exchange'

  return (
    <HorizontalTabs withBorder={true} activeTabId="button-id-1">
      <HorizontalTabsList ariaLabel="Find a car">
        <HorizontalTabsButton id="button-id-1" panelId="panel-id-1">
          <div className={styles.tabTitle}>{usedCarsTabLabel}</div>
        </HorizontalTabsButton>
        <HorizontalTabsButton
          id="button-id-2"
          panelId="panel-id-2"
          onButtonClick={() => trackEvent(tabsClickEvent())}
        >
          <div className={styles.tabTitle}>{newCarsTabLabel}</div>
        </HorizontalTabsButton>
      </HorizontalTabsList>

      <HorizontalTabsPanelList>
        <HorizontalTabsPanel
          buttonId="button-id-1"
          id="panel-id-1"
          className={styles.tabPanel}
        >
          <div className={styles.formTabContainer}>
            <Text element={'h1'} className={styles.heading}>
              Buy used cars online
            </Text>
            <Filters
              section={section}
              makes={vehiclesData.usedVehicles.makes}
              totalStock={vehiclesData.usedVehicles.totalStock}
            />
            {shouldShowNewCarsCTA ? (
              <div className={styles.newCarsCtaContainer}>
                <p>Looking for something shiny and new?</p>
                <Secondary
                  element="a"
                  data-testid="newCarsButton"
                  href={'/new-cars'}
                  isFullWidth
                  onClick={handleShopNewCarsClick}
                >
                  {'Shop new cars'}
                </Secondary>
              </div>
            ) : (
              <div className={styles.helpMeChooseCtaContainer}>
                <p>Not sure where to start?</p>
                <span>
                  {'Try our '}
                  <a
                    href="/help-me-choose/most-important"
                    className={styles.helpMeChooseCtaLink}
                    onClick={() => handleTrackEvent(usedCarsTabLabel)}
                  >
                    Help Me Choose tool
                  </a>
                </span>
              </div>
            )}
          </div>
        </HorizontalTabsPanel>
        <HorizontalTabsPanel
          buttonId="button-id-2"
          id="panel-id-2"
          className={styles.tabPanel}
        >
          {isHomeHeroFormWithNewCarsTab ? (
            <div
              className={cx(styles.formTabContainer, styles.heroFormContainer)}
            >
              <Text element={'h1'} className={styles.heading}>
                Buy new cars online
              </Text>
              <SearchCarsForm
                section={section}
                vehicleCondition={VehicleCondition.New}
                makes={vehiclesData.newVehicles.makes}
                totalStock={vehiclesData.newVehicles.totalStock}
                priceBucket={vehiclesData.newVehicles.priceBucket}
                monthlyPriceBucket={vehiclesData.newVehicles.monthlyPriceBucket}
              />
            </div>
          ) : (
            <HeroPartEx
              title={title}
              ctaText={ctaText}
              label={label}
              placeholder={placeholder}
              partExTextList={partExTextList}
            />
          )}
        </HorizontalTabsPanel>
      </HorizontalTabsPanelList>
    </HorizontalTabs>
  )
}
